import { useCallback, useEffect, useState } from 'react';
import { fetchCardCatalog, clearCardCatalogCache } from '../services/cardCatalog';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Loads the full card pool for the active language and keeps it in state.
 *
 * Refetches on language change (the feed returns localized names, effects and
 * availability per language, so the pool genuinely differs). Stale responses
 * from a previous language are discarded rather than applied out of order.
 */
export function useCardCatalog() {
  const { language } = useLanguage();

  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(
    async (force = false) => {
      setLoading(true);
      setError('');
      try {
        const cards = await fetchCardCatalog(language, { force });
        return cards;
      } finally {
        setLoading(false);
      }
    },
    [language]
  );

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError('');
    fetchCardCatalog(language)
      .then((cards) => {
        if (!cancelled) setCatalog(cards);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Card catalog load failed:', err);
        setError('Failed to load the card list. Check your connection and retry.');
        setCatalog([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [language]);

  /** Force a refetch, bypassing both caches (the "refresh cards" action). */
  const refresh = useCallback(async () => {
    clearCardCatalogCache();
    try {
      const cards = await load(true);
      setCatalog(cards);
    } catch (err) {
      console.error('Card catalog refresh failed:', err);
      setError('Failed to refresh the card list.');
    }
  }, [load]);

  return { catalog, catalogLoading: loading, catalogError: error, refreshCatalog: refresh };
}

export default useCardCatalog;
